import { TableSortValue } from "shared/TableSortHeader";

export const defaultStatusFilter = ["current"];

export function normalizeValue(value, fallback = "") {
  return String(value ?? fallback)
    .trim()
    .toLowerCase();
}

export function parseCsvFilter(value, fallback = []) {
  // null/undefined means the param was never set → use the default.
  // An empty string means the user explicitly chose "All" → no filter.
  if (value == null) {
    return fallback;
  }
  if (value === "") {
    return [];
  }

  return value
    .split(",")
    .map((item) => normalizeValue(item))
    .filter((item) => item !== "");
}

export function toCsvFilter(values) {
  if (values.length === 0) {
    // Return empty string (not null) so the param stays in the URL as an
    // explicit "show all" signal, distinguishable from "never set" (null).
    return "";
  }

  return values.join(",");
}

export function toLabel(value) {
  if (value === "none") {
    return "None";
  }

  return value
    .split(" ")
    .filter((part) => part !== "")
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(" ");
}

export function statusOf(item) {
  return normalizeValue(item.status, "none");
}

export function membershipOf(item) {
  return normalizeValue(item.membership, "none");
}

export function pinOf(item) {
  if (item.pinLevel == null) {
    return "none";
  }

  return String(item.pinLevel);
}

export function sortToHeaderValue(sort, prefix) {
  if (sort === `${prefix}-asc`) {
    return TableSortValue.ascending;
  }

  if (sort === `${prefix}-desc`) {
    return TableSortValue.descending;
  }

  return TableSortValue.none;
}

export function applyTableFilters(rows, filters) {
  const { statuses, memberships, pins } = filters;

  return rows.filter((row) => {
    const statusMatch =
      statuses.length === 0 || statuses.includes(statusOf(row));
    const membershipMatch =
      memberships.length === 0 || memberships.includes(membershipOf(row));
    const pinMatch = pins.length === 0 || pins.includes(pinOf(row));

    return statusMatch && membershipMatch && pinMatch;
  });
}

export function sortRows(rows, sort) {
  const next = [...rows];

  next.sort((a, b) => {
    switch (sort) {
      case "name-asc":
        return getName(a).localeCompare(getName(b));
      case "name-desc":
        return getName(b).localeCompare(getName(a));
      case "ibo-asc":
        return (a.iboNumber ?? 0) - (b.iboNumber ?? 0);
      case "ibo-desc":
        return (b.iboNumber ?? 0) - (a.iboNumber ?? 0);
      case "pin-asc":
        return (a.pinLevel ?? 0) - (b.pinLevel ?? 0);
      case "pin-desc":
        return (b.pinLevel ?? 0) - (a.pinLevel ?? 0);
      case "status-asc":
        return statusOf(a).localeCompare(statusOf(b));
      case "status-desc":
        return statusOf(b).localeCompare(statusOf(a));
      case "membership-asc":
        return membershipOf(a).localeCompare(membershipOf(b));
      case "membership-desc":
        return membershipOf(b).localeCompare(membershipOf(a));
      default:
        return 0;
    }
  });

  return next;
}

function getName(item) {
  if (item.fullName) {
    return normalizeValue(item.fullName);
  }

  const firstName = normalizeValue(item.firstName);
  const lastName = normalizeValue(item.lastName);
  return `${lastName} ${firstName}`.trim();
}

export function toOptions(values, forcedValues = []) {
  const merged = new Set([...values, ...forcedValues]);

  return [...merged]
    .filter((value) => value !== "")
    .sort((a, b) => {
      const aIsNumber = /^\d+$/.test(a);
      const bIsNumber = /^\d+$/.test(b);

      if (aIsNumber && bIsNumber) {
        return Number(b) - Number(a);
      }

      return a.localeCompare(b);
    })
    .map((value) => ({
      value,
      label: toLabel(value),
    }));
}
